// ----------------------------------------------------------------------------
// slicer — Slicer panel for DAWSEUR
//
// Layout: 2 columns
//   Left: list of slicer sequences (create, select, delete, rename)
//   Right: waveform + slice editor + step sequencer for slice triggers
//
// Load any bucket sample, auto-detect transients, create slice sequences.
// Sequences are embedded per-clip when dragged to the timeline.
// ----------------------------------------------------------------------------

const { makeKnob } = require('./knob.js');

// ---- Custom slider (pure JS/CSS, no native range input) ----
function makeSlider(min, max, step, value, onChange, opts) {
   opts = opts || {};
   const wrap = document.createElement('div');
   wrap.className = 'lw-custom-slider';
   wrap.style.cssText = 'display:inline-flex;align-items:center;gap:3px;font-family:bit,monospace;font-size:10px;';

   const track = document.createElement('div');
   track.style.cssText = 'position:relative;width:' + (opts.width || 80) + 'px;height:14px;background:var(--c1);border:1px solid var(--c3);cursor:pointer;';
   const fill = document.createElement('div');
   fill.style.cssText = 'position:absolute;left:0;top:0;bottom:0;background:var(--c8);';
   track.appendChild(fill);
   const thumb = document.createElement('div');
   thumb.style.cssText = 'position:absolute;top:-1px;width:6px;height:16px;background:var(--ce);border:1px solid var(--c0);pointer-events:none;';
   track.appendChild(thumb);
   wrap.appendChild(track);

   const valEl = document.createElement('span');
   valEl.style.cssText = 'color:var(--c6);min-width:' + (opts.valWidth || 36) + 'px;';
   wrap.appendChild(valEl);

   let current = value;
   const range = max - min;

   function formatVal(v) {
      if (opts.format) return opts.format(v);
      return String(v);
   }

   function setVisual() {
      const pct = ((current - min) / range) * 100;
      fill.style.width = pct + '%';
      thumb.style.left = 'calc(' + pct + '% - 3px)';
      valEl.textContent = formatVal(current);
   }

   function setValue(v) {
      current = Math.max(min, Math.min(max, v));
      current = Math.round(current / step) * step;
      setVisual();
      if (onChange) onChange(current);
   }

   setVisual();

   // Drag interaction
   let dragging = false;
   function updateFromX(clientX) {
      const rect = track.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setValue(min + pct * range);
   }
   track.addEventListener('mousedown', (e) => {
      dragging = true;
      e.preventDefault();
      updateFromX(e.clientX);
   });
   const mouseMoveHandler = (e) => {
      if (!dragging) return;
      updateFromX(e.clientX);
   };
   const mouseUpHandler = () => { dragging = false; };
   document.addEventListener('mousemove', mouseMoveHandler);
   document.addEventListener('mouseup', mouseUpHandler);

   // Wheel
   track.addEventListener('wheel', (e) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      setValue(current + dir * step * (e.shiftKey ? 5 : 1));
   }, { passive: false });

   // Double-click resets
   track.addEventListener('dblclick', () => {
      setValue(value);
   });

   wrap.getValue = () => current;
   wrap.setValue = setValue;
   wrap.destroy = () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
   };

   return wrap;
}

// ---- Transient detection (adapted from breakchop) ----
function detectTransients(audioBuffer, sensitivity = 1, minSliceMs = 30) {
   const sampleRate = audioBuffer.sampleRate;
   const minSliceSamples = (minSliceMs / 1000) * sampleRate;
   const totalSamples = audioBuffer.length;
   const numChannels = audioBuffer.numberOfChannels;

   // Mix down to mono
   const mono = new Float32Array(totalSamples);
   for (let ch = 0; ch < numChannels; ch++) {
      const chData = audioBuffer.getChannelData(ch);
      for (let i = 0; i < totalSamples; i++) {
         mono[i] += chData[i] / numChannels;
      }
   }

   // Envelope follower
   const attackTC = sampleRate * 0.0003;
   const releaseTC = sampleRate * 0.015;
   const attackCoeff = Math.exp(-1 / attackTC);
   const releaseCoeff = Math.exp(-1 / releaseTC);

   const envelope = new Float32Array(totalSamples);
   let env = 0;
   for (let i = 0; i < totalSamples; i++) {
      const x = Math.abs(mono[i]);
      if (x > env) {
         env = attackCoeff * env + (1 - attackCoeff) * x;
      } else {
         env = releaseCoeff * env + (1 - releaseCoeff) * x;
      }
      envelope[i] = env;
   }

   // Log compression
   const logEnv = new Float32Array(totalSamples);
   const logScale = 1000;
   const logBase = Math.log(1 + logScale);
   for (let i = 0; i < totalSamples; i++) {
      logEnv[i] = Math.log(1 + envelope[i] * logScale) / logBase;
   }

   // Derivative (onset detection function)
   const hop = 32;
   const numFrames = Math.floor(totalSamples / hop);
   const odf = new Float32Array(numFrames);
   for (let f = 1; f < numFrames; f++) {
      const prev = logEnv[(f - 1) * hop];
      const curr = logEnv[f * hop];
      odf[f] = Math.max(0, curr - prev);
   }

   // Smooth ODF
   const smoothOdf = new Float32Array(numFrames);
   for (let f = 0; f < numFrames; f++) {
      let sum = 0, count = 0;
      for (let k = -1; k <= 1; k++) {
         const idx = f + k;
         if (idx >= 0 && idx < numFrames) { sum += odf[idx]; count++; }
      }
      smoothOdf[f] = sum / count;
   }

   // Normalize
   let maxOdf = 0;
   for (let f = 0; f < numFrames; f++) {
      if (smoothOdf[f] > maxOdf) maxOdf = smoothOdf[f];
   }
   if (maxOdf <= 0) return [{ start: 0, end: totalSamples, name: 'slice_1' }];
   for (let f = 0; f < numFrames; f++) smoothOdf[f] /= maxOdf;

   // Peak picking
   const threshold = 0.15 / sensitivity;
   const peaks = [];
   let lastPeakSample = -minSliceSamples;

   for (let f = 2; f < numFrames - 2; f++) {
      if (smoothOdf[f] < threshold) continue;
      if (smoothOdf[f] <= smoothOdf[f - 1] || smoothOdf[f] < smoothOdf[f + 1]) continue;
      const samplePos = f * hop;
      if (samplePos - lastPeakSample < minSliceSamples) continue;

      // Refine onset
      const lookback = Math.floor(sampleRate * 0.02 / hop);
      let onsetFrame = f;
      let minEnv = logEnv[f * hop];
      for (let b = 1; b <= lookback && f - b > 0; b++) {
         const e = logEnv[(f - b) * hop];
         if (e <= minEnv) {
            minEnv = e;
            onsetFrame = f - b;
         }
         if (e > minEnv * 1.05 && b > 2) break;
      }

      const onsetSample = onsetFrame * hop;
      if (onsetSample - lastPeakSample < minSliceSamples) continue;
      peaks.push(onsetSample);
      lastPeakSample = onsetSample;
   }

   if (peaks.length === 0 || peaks[0] > minSliceSamples) {
      peaks.unshift(0);
   }

   // Create slices
   const slices = [];
   for (let i = 0; i < peaks.length; i++) {
      const start = peaks[i];
      const end = i < peaks.length - 1 ? peaks[i + 1] : totalSamples;
      slices.push({ start, end, name: 'slice_' + (i + 1) });
   }
   return slices;
}

// ---- Create the slicer panel ----
function createSlicer(opts = {}) {
   const container = document.getElementById('lw-slicer-body');
   if (!container) return null;

   const state = {
      sequences: [],        // array of sequence objects
      activeSeqIdx: 0,
      activeTab: 'waveform', // 'waveform' or 'sequencer'
      getBucketItems: opts.getBucketItems || (() => []),
      loadBuffer: opts.loadBuffer || (async () => null), // async (itemId) => AudioBuffer
      onChange: opts.onChange || (() => {}),
      getEngine: opts.getEngine || (() => null),
      resolveDropItem: opts.resolveDropItem || null,
      previewPlaying: false,
      previewSchedulerId: null,
      previewCurrentStep: 0,
      previewNextStepTime: 0,
      previewGain: null,
      previewStepCells: [],
   };

   // ---- Sequence object structure ----
   // {
   //   name: string,
   //   sampleId: string,       // bucket item id
   //   sampleName: string,     // display name
   //   audioBuffer: AudioBuffer, // decoded buffer (not serialized)
   //   slices: [{ start, end, name }],  // in samples
   //   steps: number,           // e.g. 16
   //   bpm: number,
   //   pattern: [[...sliceIdx], ...],  // per step: array of slice indices to trigger
   //   sensitivity: number,    // transient detection sensitivity
   //   minSliceMs: number,     // minimum slice length
   // }

   container.innerHTML = '';

   const wrapper = document.createElement('div');
   wrapper.className = 'lw-slicer-wrapper';
   wrapper.style.cssText = 'display:flex;flex-direction:row;width:100%;height:100%;overflow:hidden;';

   // Column 1: sequence list
   const leftCol = document.createElement('div');
   leftCol.className = 'lw-slicer-list-col';
   leftCol.style.cssText = 'width:140px;min-width:100px;border-right:1px solid var(--c2);display:flex;flex-direction:column;overflow:hidden;';

   const listHeader = document.createElement('div');
   listHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:4px;';
   listHeader.innerHTML = '<span style="font-family:bit,monospace;font-size:10px;color:var(--ce);flex:1;">Sequences</span>';
   const addBtn = document.createElement('button');
   addBtn.textContent = '+';
   addBtn.className = 'lw-btn-sm';
   addBtn.title = 'New slicer sequence';
   addBtn.onclick = () => createNewSequence();
   listHeader.appendChild(addBtn);
   leftCol.appendChild(listHeader);

   const listEl = document.createElement('div');
   listEl.style.cssText = 'flex:1;overflow-y:auto;';
   leftCol.appendChild(listEl);

   // Column 2: editor (waveform + sequencer tabs)
   const rightCol = document.createElement('div');
   rightCol.className = 'lw-slicer-editor-col';
   rightCol.style.cssText = 'flex:1;display:flex;flex-direction:column;overflow:hidden;';

   const editorHeader = document.createElement('div');
   editorHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:6px;flex-wrap:wrap;';
   rightCol.appendChild(editorHeader);

   const editorBody = document.createElement('div');
   editorBody.style.cssText = 'flex:1;overflow-y:auto;';
   rightCol.appendChild(editorBody);

   wrapper.appendChild(leftCol);
   wrapper.appendChild(rightCol);
   container.appendChild(wrapper);

   // ---- Create a new sequence ----
   function createNewSequence() {
      const seq = {
         uid: 'slicerseq_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
         name: 'Slice ' + (state.sequences.length + 1),
         sampleId: null,
         sampleName: '(no sample)',
         audioBuffer: null,
         slices: [],
         steps: 16,
         bpm: parseFloat(document.getElementById('lw-bpm').value) || 120,
         pattern: new Array(16).fill(null).map(() => []),
         subSteps: {},  // drill subdivisions: key "step:sliceIdx" → 2,3,4,6,8
         sensitivity: 1,
         minSliceMs: 30,
      };
      state.sequences.push(seq);
      state.activeSeqIdx = state.sequences.length - 1;
      renderList();
      renderEditor();
   }

   // ---- Render sequence list ----
   function renderList() {
      listEl.innerHTML = '';
      for (let i = 0; i < state.sequences.length; i++) {
         const seq = state.sequences[i];
         const item = document.createElement('div');
         item.style.cssText = 'padding:4px 8px;cursor:pointer;font-family:bit,monospace;font-size:10px;display:flex;align-items:center;gap:4px;border-bottom:1px solid var(--c2);';
         if (i === state.activeSeqIdx) {
            item.style.background = 'var(--c3)';
            item.style.color = 'var(--cf)';
         } else {
            item.style.color = 'var(--ce)';
         }
         item.textContent = seq.name;
         item.draggable = true;
         item.onclick = () => {
            if (state.previewPlaying) stopPreview();
            state.activeSeqIdx = i;
            renderList();
            renderEditor();
         };
         item.ondragstart = (e) => {
            e.dataTransfer.setData('text/dawseur-slicer', JSON.stringify({
               type: 'slicer',
               sequenceIdx: i,
            }));
            e.dataTransfer.effectAllowed = 'copy';
         };
         item.ondblclick = () => {
            const name = prompt('Sequence name:', seq.name);
            if (name) { seq.name = name; renderList(); }
         };
         const cloneBtn = document.createElement('span');
         cloneBtn.textContent = '⏈';
         cloneBtn.title = 'Clone sequence';
         cloneBtn.style.cssText = 'margin-left:auto;color:var(--c6);cursor:pointer;padding:0 2px;';
         cloneBtn.onclick = (e) => {
            e.stopPropagation();
            const clone = {
               name: seq.name + ' copy',
               sampleId: seq.sampleId,
               sampleName: seq.sampleName,
               audioBuffer: seq.audioBuffer,
               slices: seq.slices ? seq.slices.map(s => ({ ...s })) : [],
               steps: seq.steps,
               bpm: seq.bpm,
               pattern: seq.pattern ? seq.pattern.map(p => p ? [...p] : []) : [],
               subSteps: seq.subSteps ? { ...seq.subSteps } : {},
               sensitivity: seq.sensitivity,
               minSliceMs: seq.minSliceMs,
            };
            state.sequences.splice(i + 1, 0, clone);
            state.activeSeqIdx = i + 1;
            renderList();
            renderEditor();
            state.onChange();
         };
         item.appendChild(cloneBtn);

         const delBtn = document.createElement('span');
         delBtn.textContent = 'x';
         delBtn.title = 'Delete sequence';
         delBtn.style.cssText = 'color:var(--c6);cursor:pointer;padding:0 2px;';
         delBtn.onclick = (e) => {
            e.stopPropagation();
            state.sequences.splice(i, 1);
            if (state.activeSeqIdx >= state.sequences.length) state.activeSeqIdx = state.sequences.length - 1;
            if (state.previewPlaying) stopPreview();
            renderList();
            renderEditor();
         };
         item.appendChild(delBtn);
         listEl.appendChild(item);
      }
      if (state.sequences.length === 0) {
         listEl.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">No sequences. Click + to create one.</div>';
      }
   }

   // ---- Assign sample to a sequence ----
   async function assignSample(seq, sampleId, sampleName) {
      seq.sampleId = sampleId;
      seq.sampleName = sampleName;
      seq.audioBuffer = await state.loadBuffer(sampleId);
      if (seq.audioBuffer) {
         seq.slices = detectTransients(seq.audioBuffer, seq.sensitivity, seq.minSliceMs);
         seq.pattern = new Array(seq.steps).fill(null).map(() => []);
      }
      state.onChange();
      renderEditor();
   }

   // ---- Render editor ----
   function renderEditor() {
      const seq = state.sequences[state.activeSeqIdx];
      editorHeader.innerHTML = '';
      editorBody.innerHTML = '';
      if (!seq) {
         editorBody.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">Select or create a sequence.</div>';
         return;
      }

      // ---- Tab bar ----
      const tabBar = document.createElement('div');
      tabBar.style.cssText = 'display:flex;gap:0;';

      const tabWave = document.createElement('button');
      tabWave.textContent = 'Waveform';
      tabWave.style.cssText = `border:none;border-bottom:2px solid ${state.activeTab === 'waveform' ? 'var(--cb)' : 'transparent'};background:${state.activeTab === 'waveform' ? 'var(--c3)' : 'transparent'};color:${state.activeTab === 'waveform' ? 'var(--cf)' : 'var(--c6)'};font-family:bit,monospace;font-size:10px;padding:4px 10px;cursor:pointer;white-space:nowrap;`;
      tabWave.onclick = () => { state.activeTab = 'waveform'; renderEditor(); };
      tabBar.appendChild(tabWave);

      const tabSeq = document.createElement('button');
      tabSeq.textContent = 'Sequencer';
      tabSeq.style.cssText = `border:none;border-bottom:2px solid ${state.activeTab === 'sequencer' ? 'var(--cb)' : 'transparent'};background:${state.activeTab === 'sequencer' ? 'var(--c3)' : 'transparent'};color:${state.activeTab === 'sequencer' ? 'var(--cf)' : 'var(--c6)'};font-family:bit,monospace;font-size:10px;padding:4px 10px;cursor:pointer;white-space:nowrap;`;
      tabSeq.onclick = () => { state.activeTab = 'sequencer'; renderEditor(); };
      tabBar.appendChild(tabSeq);

      editorHeader.appendChild(tabBar);

      if (state.activeTab === 'waveform') {
         renderWaveformTab(seq);
      } else {
         renderSequencerTab(seq);
      }
   }

   // ---- Waveform tab: sample selector + peak detect ----
   function renderWaveformTab(seq) {
      const body = document.createElement('div');
      body.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:8px;';

      // Slice count
      const sliceCount = document.createElement('span');
      sliceCount.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--c6);';
      sliceCount.textContent = seq.slices.length + ' slices';
      body.appendChild(sliceCount);

      // Sensitivity row
      const sensRow = document.createElement('div');
      sensRow.style.cssText = 'display:flex;align-items:center;gap:6px;';

      const sensLabel = document.createElement('span');
      sensLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);';
      sensLabel.textContent = 'Sensitivity:';
      sensRow.appendChild(sensLabel);

      const sensSlider = makeSlider(0.2, 3, 0.1, seq.sensitivity, (v) => {
         seq.sensitivity = v;
         if (seq.audioBuffer) {
            seq.slices = detectTransients(seq.audioBuffer, seq.sensitivity, seq.minSliceMs);
            seq.pattern = new Array(seq.steps).fill(null).map(() => []);
            sliceCount.textContent = seq.slices.length + ' slices';
            state.onChange();
            renderEditor();
         }
      }, { format: v => v.toFixed(1), valWidth: 30 });
      sensRow.appendChild(sensSlider);

      // Min slice ms
      const minMsLabel = document.createElement('span');
      minMsLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);margin-left:8px;';
      minMsLabel.textContent = 'Min slice ms:';
      sensRow.appendChild(minMsLabel);

      const minMsSlider = makeSlider(10, 200, 5, seq.minSliceMs, (v) => {
         seq.minSliceMs = v;
         if (seq.audioBuffer) {
            seq.slices = detectTransients(seq.audioBuffer, seq.sensitivity, seq.minSliceMs);
            seq.pattern = new Array(seq.steps).fill(null).map(() => []);
            sliceCount.textContent = seq.slices.length + ' slices';
            state.onChange();
            renderEditor();
         }
      }, { format: v => v + 'ms', valWidth: 36 });
      sensRow.appendChild(minMsSlider);

      body.appendChild(sensRow);

      // Waveform / dropzone area — accepts drag & drop from bucket
      const waveDiv = document.createElement('div');
      waveDiv.style.cssText = 'position:relative;width:100%;height:120px;border:1px solid var(--c2);';
      body.appendChild(waveDiv);

      if (seq.audioBuffer) {
         // Show waveform
         const canvas = document.createElement('canvas');
         canvas.style.cssText = 'width:100%;height:100%;display:block;';
         canvas.width = waveDiv.clientWidth || 400;
         canvas.height = 120;
         waveDiv.appendChild(canvas);
         drawWaveform(canvas, seq.audioBuffer, seq.slices);

         const resizeObserver = new ResizeObserver(() => {
            canvas.width = waveDiv.clientWidth || 400;
            drawWaveform(canvas, seq.audioBuffer, seq.slices);
         });
         resizeObserver.observe(waveDiv);
      } else {
         // Show dropzone hint
         waveDiv.style.cssText += 'display:flex;align-items:center;justify-content:center;border:1px dashed var(--c4);';
         const hint = document.createElement('div');
         hint.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--c6);text-align:center;';
         hint.innerHTML = 'Drag & drop a sample here<br>or select from the dropdown above';
         waveDiv.appendChild(hint);
      }

      // Drag & drop onto waveform area
      ['dragenter', 'dragover'].forEach((evt) => {
         waveDiv.addEventListener(evt, (e) => {
            const isBucketItem = e.dataTransfer.types.includes('application/x-bucket-item-id');
            const hasFiles = e.dataTransfer.types.includes('Files');
            const hasText = e.dataTransfer.types.includes('text/plain');
            if (isBucketItem || hasFiles || hasText) {
               e.preventDefault();
               e.dataTransfer.dropEffect = 'copy';
               waveDiv.style.borderColor = 'var(--c9)';
               waveDiv.style.background = 'var(--c1)';
            }
         });
      });
      waveDiv.addEventListener('dragleave', () => {
         waveDiv.style.borderColor = '';
         waveDiv.style.background = '';
      });
      waveDiv.addEventListener('drop', async (e) => {
         e.preventDefault();
         waveDiv.style.borderColor = '';
         waveDiv.style.background = '';
         if (state.resolveDropItem) {
            const item = await state.resolveDropItem(e.dataTransfer);
            if (item) {
               await assignSample(seq, item.id, item.name);
               return;
            }
         }
         const itemId = e.dataTransfer.getData('application/x-bucket-item-id') ||
                        e.dataTransfer.getData('text/plain');
         if (itemId && state.getBucketItems) {
            const item = state.getBucketItems().find(it => it.id === itemId);
            if (item) {
               await assignSample(seq, item.id, item.name);
               return;
            }
         }
      });

      // Slice list (only if we have slices)
      if (seq.audioBuffer && seq.slices.length > 0) {
         const sliceListHeader = document.createElement('div');
         sliceListHeader.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);margin-top:4px;';
         sliceListHeader.textContent = 'Slices (click to preview):';
         body.appendChild(sliceListHeader);

         const sliceList = document.createElement('div');
         sliceList.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;max-height:120px;overflow-y:auto;';
         for (let si = 0; si < seq.slices.length; si++) {
            const slice = seq.slices[si];
            const tag = document.createElement('div');
            const dur = ((slice.end - slice.start) / seq.audioBuffer.sampleRate * 1000).toFixed(1);
            tag.style.cssText = 'font-family:bit,monospace;font-size:9px;color:var(--ce);background:var(--c1);border:1px solid var(--c2);padding:1px 4px;cursor:pointer;';
            tag.textContent = slice.name + ' (' + dur + 'ms)';
            tag.onclick = () => {
               const eng = state.getEngine ? state.getEngine() : null;
               if (eng && seq.audioBuffer) {
                  const ctx = eng.ctx;
                  const src = ctx.createBufferSource();
                  src.buffer = seq.audioBuffer;
                  const gain = ctx.createGain();
                  gain.gain.value = 0.8;
                  src.connect(gain);
                  gain.connect(ctx.destination);
                  const offset = slice.start / seq.audioBuffer.sampleRate;
                  const durSec = (slice.end - slice.start) / seq.audioBuffer.sampleRate;
                  src.start(0, offset, durSec);
               }
            };
            sliceList.appendChild(tag);
         }
         body.appendChild(sliceList);
      }

      editorBody.appendChild(body);
   }

   // ---- Preview player ----
   function startPreview() {
      const seq = state.sequences[state.activeSeqIdx];
      if (!seq || !seq.audioBuffer || seq.slices.length === 0) return;
      const eng = state.getEngine();
      if (!eng) return;

      stopPreview();

      if (eng.state.isPlaying) eng.stop();

      state.previewPlaying = true;
      state.previewCurrentStep = 0;
      const ctx = eng.ctx;
      if (ctx.state === 'suspended') ctx.resume();

      const secPerBeat = 60 / (seq.bpm || parseFloat(document.getElementById('lw-bpm').value) || 120);
      const secPerStep = secPerBeat / 4;
      state.previewNextStepTime = ctx.currentTime + 0.2;

      const previewGain = ctx.createGain();
      previewGain.gain.value = 0.8;
      previewGain.connect(ctx.destination);
      state.previewGain = previewGain;

      const sampleRate = seq.audioBuffer.sampleRate;

      function scheduler() {
         if (!state.previewPlaying) return;
         try {
            const lookAhead = 0.2;
            while (state.previewNextStepTime < ctx.currentTime + lookAhead) {
               const step = state.previewCurrentStep;
               const whenSec = state.previewNextStepTime;

               // Trigger active slices for this step
               const sliceIndices = seq.pattern[step];
               if (sliceIndices) {
                  for (const si of sliceIndices) {
                     if (si < 0 || si >= seq.slices.length) continue;
                     const slice = seq.slices[si];
                     const offset = slice.start / sampleRate;
                     const durSec = (slice.end - slice.start) / sampleRate;
                     const drillKey = step + ':' + si;
                     const subs = (seq.subSteps && seq.subSteps[drillKey]) || 0;
                     const vel = (seq.velocities && seq.velocities[drillKey] !== undefined) ? seq.velocities[drillKey] : 1.0;
                     const amp = 0.8 * vel;
                     if (subs > 1) {
                        // Drill: retrigger subs times evenly within the step (mono per slice)
                        const subDur = secPerStep / subs;
                        for (let i = 0; i < subs; i++) {
                           if (eng.triggerSampleMono) {
                              eng.triggerSampleMono('slicer:' + si, seq.audioBuffer, previewGain, whenSec + i * subDur, amp, 1.0, 0, offset, durSec);
                           } else {
                              eng.triggerSample(seq.audioBuffer, previewGain, whenSec + i * subDur, amp, 1.0, 0, offset, durSec);
                           }
                        }
                     } else {
                        if (eng.triggerSampleMono) {
                           eng.triggerSampleMono('slicer:' + si, seq.audioBuffer, previewGain, whenSec, amp, 1.0, 0, offset, durSec);
                        } else {
                           eng.triggerSample(seq.audioBuffer, previewGain, whenSec, amp, 1.0, 0, offset, durSec);
                        }
                     }
                  }
               }

               // Schedule UI highlight
               const highlightStep = step;
               const highlightTime = whenSec - ctx.currentTime;
               setTimeout(() => {
                  if (state.previewPlaying) highlightStepUI(highlightStep);
               }, Math.max(0, highlightTime * 1000));

               state.previewCurrentStep = (state.previewCurrentStep + 1) % seq.steps;
               state.previewNextStepTime += secPerStep;
            }
         } catch (e) {}
         state.previewSchedulerId = setTimeout(scheduler, 25);
      }
      scheduler();
      updatePreviewButton();
   }

   function stopPreview() {
      state.previewPlaying = false;
      if (state.previewSchedulerId) {
         clearTimeout(state.previewSchedulerId);
         state.previewSchedulerId = null;
      }
      clearStepHighlight();
      updatePreviewButton();
   }

   function togglePreview() {
      if (state.previewPlaying) stopPreview();
      else startPreview();
   }

   function updatePreviewButton() {
      const btn = document.getElementById('lw-slicer-preview-btn');
      if (btn) {
         btn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
         btn.style.color = state.previewPlaying ? 'var(--c6)' : 'var(--c9)';
      }
   }

   function highlightStepUI(step) {
      // Highlight header cells
      for (const cell of state.previewStepCells) {
         if (cell) cell.style.color = 'var(--c6)';
      }
      if (state.previewStepCells[step]) {
         state.previewStepCells[step].style.color = 'var(--cb)';
      }
      // Highlight the step column cells
      const grid = editorBody.querySelector('.lw-slicer-seq-grid');
      if (grid) {
         grid.querySelectorAll('.lw-seq-cell-playing').forEach(el => el.classList.remove('lw-seq-cell-playing'));
         grid.querySelectorAll(`.lw-slicer-seq-cell[data-step="${step}"]`).forEach(el => el.classList.add('lw-seq-cell-playing'));
      }
   }

   function clearStepHighlight() {
      for (const cell of state.previewStepCells) {
         if (cell) cell.style.color = '';
      }
      const grid = editorBody.querySelector('.lw-slicer-seq-grid');
      if (grid) {
         grid.querySelectorAll('.lw-seq-cell-playing').forEach(el => el.classList.remove('lw-seq-cell-playing'));
      }
   }

   const DRILL_CYCLE = [0, 2, 3, 4, 6, 8];

   function cycleSubSteps(seq, step, sliceIdx) {
      if (!seq.pattern[step] || !seq.pattern[step].includes(sliceIdx)) {
         seq.pattern[step] = seq.pattern[step] || [];
         seq.pattern[step].push(sliceIdx);
      }
      const key = step + ':' + sliceIdx;
      const current = (seq.subSteps && seq.subSteps[key]) || 0;
      const currentIdx = DRILL_CYCLE.indexOf(current);
      const nextIdx = (currentIdx + 1) % DRILL_CYCLE.length;
      const nextVal = DRILL_CYCLE[nextIdx];
      if (!seq.subSteps) seq.subSteps = {};
      if (nextVal === 0) {
         delete seq.subSteps[key];
      } else {
         seq.subSteps[key] = nextVal;
      }
      state.onChange();
      renderEditor();
   }

   // ---- Sequencer tab: step grid for slice triggers ----
   function renderSequencerTab(seq) {
      const body = document.createElement('div');
      body.style.cssText = 'padding:8px;display:flex;flex-direction:column;gap:4px;';

      if (!seq.audioBuffer || seq.slices.length === 0) {
         const hint = document.createElement('div');
         hint.style.cssText = 'padding:16px;font-family:bit,monospace;font-size:10px;color:var(--c6);text-align:center;';
         hint.textContent = 'Load a sample in the Waveform tab first';
         body.appendChild(hint);
         editorBody.appendChild(body);
         return;
      }

      const steps = seq.steps;

      // ---- Toolbar ----
      const toolbar = document.createElement('div');
      toolbar.style.cssText = 'display:flex;align-items:center;gap:6px;margin-bottom:4px;flex-wrap:wrap;';

      const stepsLabel = document.createElement('span');
      stepsLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);';
      stepsLabel.textContent = 'Steps:';
      toolbar.appendChild(stepsLabel);

      const stepsSelect = document.createElement('select');
      stepsSelect.style.cssText = 'background:var(--c0);border:1px solid var(--c4);color:var(--cf);font-family:bit,monospace;font-size:10px;';
      [8, 16, 24, 32].forEach(n => {
         const opt = document.createElement('option');
         opt.value = n; opt.textContent = n;
         if (n === steps) opt.selected = true;
         stepsSelect.appendChild(opt);
      });
      stepsSelect.onchange = () => {
         const newSteps = parseInt(stepsSelect.value);
         if (seq.pattern.length < newSteps) {
            seq.pattern = seq.pattern.concat(new Array(newSteps - seq.pattern.length).fill(null).map(() => []));
         } else {
            seq.pattern = seq.pattern.slice(0, newSteps);
         }
         seq.steps = newSteps;
         renderEditor();
         state.onChange();
      };
      toolbar.appendChild(stepsSelect);

      const sliceCount = document.createElement('span');
      sliceCount.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--c6);margin-left:4px;';
      sliceCount.textContent = seq.slices.length + ' slices';
      toolbar.appendChild(sliceCount);

      // Play/stop preview
      const playBtn = document.createElement('button');
      playBtn.id = 'lw-slicer-preview-btn';
      playBtn.className = 'lw-btn-sm';
      playBtn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
      playBtn.style.cssText = 'margin-left:8px;color:' + (state.previewPlaying ? 'var(--c6)' : 'var(--c9)') + ';font-family:icons;';
      playBtn.onclick = (e) => { e.stopPropagation(); togglePreview(); };
      toolbar.appendChild(playBtn);

      // Clear button
      const clearBtn = document.createElement('button');
      clearBtn.textContent = 'Clear';
      clearBtn.className = 'lw-btn-text';
      clearBtn.style.cssText = 'margin-left:4px;padding:2px 8px;';
      clearBtn.onclick = () => {
         seq.pattern = new Array(steps).fill(null).map(() => []);
         seq.subSteps = {};
         seq.velocities = {};
         state.onChange();
         renderEditor();
      };
      toolbar.appendChild(clearBtn);

      // Velocity mode toggle
      const velModeBtn = document.createElement('button');
      velModeBtn.textContent = 'VEL';
      velModeBtn.className = 'lw-btn-text';
      velModeBtn.style.cssText = 'margin-left:4px;padding:2px 8px;' + (state.velMode ? 'background:var(--cb);color:var(--c0);' : '');
      velModeBtn.title = 'Toggle velocity edit mode (knobs under active steps)';
      velModeBtn.onclick = () => {
         state.velMode = !state.velMode;
         renderEditor();
      };
      toolbar.appendChild(velModeBtn);

      body.appendChild(toolbar);

      // ---- Sequencer grid (breakchop-style) ----
      const grid = document.createElement('div');
      grid.className = 'lw-slicer-seq-grid';

      // Header row (step numbers)
      state.previewStepCells = [];
      const headerRow = document.createElement('div');
      headerRow.className = 'lw-slicer-seq-header-row';
      headerRow.style.gridTemplateColumns = `60px repeat(${steps}, 1fr)`;

      const labelCell = document.createElement('div');
      labelCell.className = 'lw-slicer-seq-row-label';
      headerRow.appendChild(labelCell);

      for (let s = 0; s < steps; s++) {
         const cell = document.createElement('div');
         cell.className = 'lw-slicer-seq-header-cell';
         if (s % 4 === 0) cell.classList.add('lw-seq-beat-start');
         cell.textContent = s + 1;
         state.previewStepCells.push(cell);
         headerRow.appendChild(cell);
      }
      grid.appendChild(headerRow);

      // One row per slice
      const maxSlices = Math.min(seq.slices.length, 32);
      for (let si = 0; si < maxSlices; si++) {
         const slice = seq.slices[si];
         const row = document.createElement('div');
         row.className = 'lw-slicer-seq-row';
         row.style.gridTemplateColumns = `60px repeat(${steps}, 1fr)`;

         const label = document.createElement('div');
         label.className = 'lw-slicer-seq-row-label';
         label.textContent = slice.name;
         label.title = slice.name + ' (' + ((slice.end - slice.start) / seq.audioBuffer.sampleRate * 1000).toFixed(1) + 'ms)';
         row.appendChild(label);

         for (let s = 0; s < steps; s++) {
            const cell = document.createElement('div');
            cell.className = 'lw-slicer-seq-cell';
            if (s % 4 === 0) cell.classList.add('lw-seq-beat-start');

            const isActive = seq.pattern[s] && seq.pattern[s].includes(si);
            if (isActive) cell.classList.add('lw-seq-cell-active');

            const drillKey = s + ':' + si;
            const drillSubs = (seq.subSteps && seq.subSteps[drillKey]) || 0;
            if (drillSubs > 0) {
               cell.classList.add('lw-seq-cell-drill');
               cell.textContent = drillSubs;
            }

            cell.dataset.step = s;
            cell.dataset.slice = si;

            cell.addEventListener('click', () => {
               if (!seq.pattern[s]) seq.pattern[s] = [];
               const idx = seq.pattern[s].indexOf(si);
               if (idx >= 0) {
                  seq.pattern[s].splice(idx, 1);
                  if (seq.subSteps) delete seq.subSteps[drillKey];
                  if (seq.velocities) delete seq.velocities[drillKey];
               } else {
                  seq.pattern[s].push(si);
               }
               state.onChange();
               renderEditor();
            });

            cell.addEventListener('contextmenu', (e) => {
               e.preventDefault();
               e.stopPropagation();
               cycleSubSteps(seq, s, si);
            });

            row.appendChild(cell);
         }
         grid.appendChild(row);

         // Velocity knob row (only when VEL mode is active)
         if (state.velMode) {
            const velRow = document.createElement('div');
            velRow.className = 'lw-slicer-seq-row';
            velRow.style.gridTemplateColumns = `60px repeat(${steps}, 1fr)`;
            velRow.style.minHeight = '24px';
            // Empty label cell
            velRow.appendChild(document.createElement('div'));
            for (let s = 0; s < steps; s++) {
               const drillKey = s + ':' + si;
               const isActive = seq.pattern[s] && seq.pattern[s].includes(si);
               const velWrap = document.createElement('div');
               velWrap.style.cssText = 'display:flex;justify-content:center;align-items:center;';
               if (isActive) {
                  if (!seq.velocities) seq.velocities = {};
                  const vel = seq.velocities[drillKey] !== undefined ? seq.velocities[drillKey] : 1.0;
                  const velKnob = makeKnob('', 0, 1, 0.01, vel, (v) => {
                     seq.velocities[drillKey] = v;
                     state.onChange();
                  }, null, { noLabels: true, id: 'slicer_' + drillKey + '_vel' });
                  velKnob.style.cssText = 'transform:scale(0.5);transform-origin:center;';
                  velWrap.appendChild(velKnob);
               }
               velRow.appendChild(velWrap);
            }
            grid.appendChild(velRow);
         }
      }

      body.appendChild(grid);

      // Hint text
      const hint = document.createElement('div');
      hint.style.cssText = 'font-family:bit,monospace;font-size:9px;color:var(--c6);margin-top:4px;';
      hint.textContent = 'Left-click: toggle step | Right-click: cycle drill (2/3/4/6/8) | VEL mode: knobs to set velocity';
      body.appendChild(hint);

      editorBody.appendChild(body);
   }


   // ---- Draw waveform with slice markers ----
   function drawWaveform(canvas, audioBuffer, slices) {
      const ctx = canvas.getContext('2d');
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const numChannels = audioBuffer.numberOfChannels;
      const totalSamples = audioBuffer.length;
      const samplesPerPixel = totalSamples / w;

      // Get theme colors
      const c0 = getComputedStyle(document.documentElement).getPropertyValue('--c0') || '#111';
      const c9 = getComputedStyle(document.documentElement).getPropertyValue('--c9') || '#4a9';
      const c6 = getComputedStyle(document.documentElement).getPropertyValue('--c6') || '#666';

      ctx.fillStyle = c0.trim();
      ctx.fillRect(0, 0, w, h);

      // Draw waveform
      ctx.strokeStyle = c9.trim();
      ctx.lineWidth = 1;
      ctx.beginPath();
      const midY = h / 2;
      for (let x = 0; x < w; x++) {
         const startSample = Math.floor(x * samplesPerPixel);
         const endSample = Math.floor((x + 1) * samplesPerPixel);
         let max = 0;
         for (let ch = 0; ch < numChannels; ch++) {
            const data = audioBuffer.getChannelData(ch);
            for (let i = startSample; i < endSample && i < totalSamples; i += 32) {
               const v = Math.abs(data[i]);
               if (v > max) max = v;
            }
         }
         const y = max * midY * 0.9;
         ctx.moveTo(x + 0.5, midY - y);
         ctx.lineTo(x + 0.5, midY + y);
      }
      ctx.stroke();

      // Draw slice markers
      ctx.strokeStyle = c6.trim();
      ctx.lineWidth = 1;
      for (const slice of slices) {
         const x = (slice.start / totalSamples) * w;
         ctx.beginPath();
         ctx.moveTo(x, 0);
         ctx.lineTo(x, h);
         ctx.stroke();
      }
   }

   // ---- Get active sequence ----
   function getActiveSequence() {
      return state.sequences[state.activeSeqIdx] || null;
   }

   // ---- Get all sequences (for saving) ----
   function getSequences() {
      // Don't serialize audioBuffer
      return state.sequences.map(s => {
         const { audioBuffer, ...rest } = s;
         return rest;
      });
   }

   // Get live sequence object by index (reference, not copy)
   function getSequenceRef(idx) {
      return state.sequences[idx] || null;
   }

   // ---- Load sequences ----
   function loadSequences(seqs) {
      state.sequences = seqs || [];
      // Ensure all sequences have UIDs (for live-reference linking on load)
      for (const seq of state.sequences) {
         if (!seq.uid) {
            seq.uid = 'slicerseq_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
         }
      }
      state.activeSeqIdx = 0;
      renderList();
      renderEditor();
   }

   // ---- Initialize ----
   if (state.sequences.length === 0) {
      createNewSequence();
   } else {
      renderList();
      renderEditor();
   }

   return {
      getActiveSequence,
      getSequences,
      getSequenceRef,
      loadSequences,
      stopPreview,
      render: () => { renderList(); renderEditor(); },
   };
}

module.exports = { createSlicer, detectTransients };
